import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { BsCheck, BsCheckAll, BsClock } from 'react-icons/bs';
import { FiMoreVertical, FiEdit2, FiTrash2, FiCopy, FiCornerUpRight } from 'react-icons/fi';
import Avatar from '../Shared/Avatar';
import { getAttachmentKind, getAttachmentIcon, formatFileSize } from '../attachmentKind';
import '../TalkAttachmentModals.css';
import { useI18n } from '../../../i18n/I18nContext';
import useAnchoredMessageMenu from '../../../hooks/useAnchoredMessageMenu';



/**
 * MessageBubble Component
 * Displays a single message with read receipts, timestamps, and actions
 *
 * @param {Object} props
 * @param {Object} props.message - Message data
 * @param {boolean} props.isSent - Whether message was sent by current user
 * @param {boolean} props.isGrouped - Whether message is grouped with previous message
 * @param {Object} props.sender - Message sender info
 * @param {boolean} props.showAvatar - Whether to show avatar (for received messages)
 * @param {function} props.onEdit - Edit message handler
 * @param {function} props.onDelete - Delete message handler
 * @param {function} props.onForward - Forward message handler
 */
const MessageBubble = ({
  message,
  isSent = false,
  isGrouped = false,
  sender,
  showAvatar = true,
  onEdit,
  onDelete,
  onForward,
  onAttachmentClick
}) => {
  const { t } = useI18n();
  const [showActions, setShowActions] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [copyError, setCopyError] = useState('');
  const [compact, setCompact] = useState(() => !window.matchMedia || window.matchMedia('(max-width: 1024px)').matches);
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const available = !message.deleted_at && message.status !== 'sending' && message.status !== 'failed';
  const closeMenu = useCallback(() => setShowMenu(false), []);
  const positionClass = useAnchoredMessageMenu(showMenu && available, triggerRef, menuRef, closeMenu);

  useEffect(() => {
    if (!available) {
      setShowMenu(false);
      setShowActions(false);
    }
  }, [available]);

  useEffect(() => {
    if (!window.matchMedia) return;
    const media = window.matchMedia('(max-width: 1024px)');
    const update = () => setCompact(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!showActions && !showMenu) return;
    const outside = event => {
      if (!wrapperRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setShowMenu(false);
        setShowActions(false);
      }
    };
    const keydown = event => {
      if (event.key === 'Escape') {
        setShowMenu(false);
        triggerRef.current?.focus();
      }
      if (event.key === 'Tab' && showMenu) {
        setShowMenu(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', keydown);
    if (showMenu) menuRef.current?.querySelector('button')?.focus();
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', keydown);
    };
  }, [showActions, showMenu, compact]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false
    });
  };

  const getReadReceiptIcon = () => {
    // TODO: Implement actual read receipt logic from backend
    const isRead = false;
    const isDelivered = true;
    const isSending = message.status === 'sending';

    if (isSending) {
      return <BsClock className="message-status-icon message-status-sending" size={12} />;
    }

    if (isRead) {
      return <BsCheckAll className="message-status-icon message-status-read" size={16} />;
    }

    if (isDelivered) {
      return <BsCheckAll className="message-status-icon message-status-delivered" size={16} />;
    }

    return <BsCheck className="message-status-icon message-status-sent" size={16} />;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.body);
      setCopyError('');
      setShowMenu(false);
    } catch {
      setCopyError('Could not copy text. Please select the message and copy it manually.');
    }
  };

  const handleMenuAction = (action) => {
    if (action !== 'copy') setShowMenu(false);
    switch (action) {
      case 'edit':
        onEdit?.(message);
        break;
      case 'delete':
        onDelete?.(message);
        break;
      case 'forward':
        onForward?.(message);
        break;
      case 'copy':
        handleCopy();
        break;
      default:
        break;
    }
  };

  const canEdit = isSent && message.kind === 'text' && !message.deleted_at;
  const canDelete = message.can_delete_for_me !== false;
  const menu = (
    <div ref={menuRef} role="dialog" aria-label="Message options"
      className={`talk-dropdown-menu talk-message-menu talk-message-menu-dropdown ${positionClass}`}>
      {!message.deleted_at && message.body && <button className="talk-dropdown-item" onClick={() => handleMenuAction('copy')}><FiCopy size={14} /> Copy text</button>}
      {canEdit && <button className="talk-dropdown-item" onClick={() => handleMenuAction('edit')}><FiEdit2 size={14} /> Edit</button>}
      {canDelete && <button className="talk-dropdown-item talk-dropdown-item-danger" onClick={() => handleMenuAction('delete')}><FiTrash2 size={14} /> Delete</button>}
      {copyError && <p className="talk-action-error" role="alert">{copyError}</p>}
    </div>
  );

  return (
    <div
      className={`talk-message-wrapper ${isSent ? 'talk-message-sent' : 'talk-message-received'} ${isGrouped ? 'talk-message-grouped' : ''}`}
      ref={wrapperRef}
      tabIndex={0}
      onFocus={() => setShowActions(true)}
      onClick={() => setShowActions(true)}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => {
        if (!showMenu && !compact) setShowActions(false);
      }}
    >
      {!isGrouped && showAvatar && (
        <Avatar
          src={sender?.avatar}
          name={sender?.name || 'User'}
          size={32}
          className="talk-message-avatar"
        />
      )}

      {isGrouped && showAvatar && (
        <div className="talk-message-avatar-spacer" />
      )}

      <div className="talk-message-content">
        {/* Show sender name for all messages */}
        {!isGrouped && sender && (
          <div className="talk-message-sender-name">
            {sender.name}
          </div>
        )}

        <div className="talk-message-bubble">
          {!message.deleted_at && message.meta?.forwarded && <div className="talk-message-forwarded"><FiCornerUpRight size={12} /> Forwarded</div>}
          {message.deleted_at && <div className="talk-message-deleted">
            <FiTrash2 aria-hidden="true" />
            {isSent ? t('talk.deletion.youDeleted', {}, 'You deleted this message') : t('talk.deletion.deleted', {}, 'This message was deleted')}
          </div>}
          {/* Message body */}
          {!message.deleted_at && message.body && (
            <div className="talk-message-text">
              {message.body}
            </div>
          )}

          {/* Attachments */}
          {!message.deleted_at && message.uploads && message.uploads.length > 0 && (
            <div className="talk-message-attachments">
              {message.uploads.map((upload) => {
                const kind = getAttachmentKind(upload.name);
                const handleClick = () => onAttachmentClick?.(message, upload, kind);

                if (kind === 'image') {
                  return (
                    <img
                      key={upload.id}
                      src={upload.url}
                      alt={upload.name}
                      title={upload.name}
                      className="talk-message-attachment-thumb"
                      onClick={handleClick}
                    />
                  );
                }

                const Icon = getAttachmentIcon(upload.name);
                return (
                  <div
                    key={upload.id}
                    className="talk-message-attachment-card"
                    onClick={handleClick}
                  >
                    <Icon className="talk-message-attachment-file-icon" />
                    <div className="talk-message-attachment-info">
                      <span className="talk-message-attachment-filename">{upload.name}</span>
                      <span className="talk-message-attachment-filesize">{formatFileSize(upload.size)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Metadata: time and read receipts */}
          <div className="talk-message-meta">
            {!message.deleted_at && message.edited_at && (
              <span className="talk-message-edited">edited</span>
            )}
            <span className="talk-message-time">
              {formatTime(message.created_at)}
            </span>
            {isSent && getReadReceiptIcon()}
          </div>
        </div>

        {/* Message actions (appear on hover) */}
        {(showActions || showMenu) && available && (
          <div className={`talk-message-actions ${isSent ? 'talk-message-actions-sent' : 'talk-message-actions-received'}`}>
            {!message.deleted_at && <button
              className="talk-message-action-btn"
              onClick={() => handleMenuAction('forward')}
              aria-label="Forward message"
              title="Forward message"
            >
              <FiCornerUpRight size={14} />
            </button>}

            <div className="talk-menu-container">
              <button
                ref={triggerRef}
                className="talk-message-action-btn"
                onClick={() => setShowMenu(!showMenu)}
                aria-label="More options"
                title="More"
                aria-expanded={showMenu}
                aria-haspopup="dialog"
              >
                <FiMoreVertical size={14} />
              </button>

              {showMenu && available && createPortal(menu, document.body)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
